import { ASSIGNED_DIFFICULTIES, sectionLabel } from '@all-for-one/shared-types'
import React, { useState, useCallback, useMemo } from 'react'
import { Question } from 'types'

import { DifficultyRow } from 'lesson-builder/components/difficulty-row'
import { QuestionContainer } from 'lesson-builder/components/question-container'

import 'css/components/lesson-builder/question-slide.css'
import { useEditQuestionEduqueryMutation } from 'util/rtk-api'
import { useLesson } from 'lesson-builder/context'

/**
 * Main question slide component
 * @param {Object} props
 * @param {number} [props.slideIndex] - Index of the slide
 * @param {Object} [props.slideQuestions] - Questions for each difficulty level
 * @param {string} [props.learningStage] - Learning stage of the question
 * @param {function} [props.onApproveQuestion] - Function to call when the question is approved
 * @param {function} [props.dispatch] - Function to dispatch actions to the store
 * @param {boolean} [props.canDelete] - Whether delete is available (Draft mode, no results)
 * @param {function} [props.onDeleteQuestion] - Function to call when a question is deleted
 */
export const QuestionSlide = ({ slideIndex, slideQuestions = [], learningStage = 'Pre-Assessment', onApproveQuestion, onApproveAll, dispatch, canDelete, onDeleteQuestion, canEdit }) => {
	const [currentDifficulty, setCurrentDifficulty] = useState(ASSIGNED_DIFFICULTIES.EMERGING)
	const { lessonId } = useLesson()
	const [editQuestionEduquery] = useEditQuestionEduqueryMutation()

	const handleDifficultyChange = useCallback((difficulty) => {
		setCurrentDifficulty(difficulty)
	}, [])

	// Memoize current question to prevent unnecessary recalculations
	const currentDifficultyQuestions = useMemo(() =>
		slideQuestions.filter(difficultyQuestions => difficultyQuestions.difficulty === currentDifficulty)[0], [slideQuestions, currentDifficulty]
	)

    const handleApprove = useCallback((questionIndex, difficulty, approved) => {
        onApproveQuestion(questionIndex, difficulty, approved)
    }, [onApproveQuestion])

	const isAssessmentSlide = learningStage === 'Pre-Assessment' || learningStage === 'Post-Assessment';

	const handleEdit = useCallback((questionIndex: number, difficulty: string, payload: Partial<Question>) => {
		// Look up question using passed difficulty parameter instead of memoized currentDifficultyQuestions
		// This makes the callback more stable (fewer dependency changes)
		const difficultyGroup = slideQuestions.find(dq => dq.difficulty === difficulty)
		const originalQuestion: Question | undefined = difficultyGroup?.questions?.[questionIndex]
		if (!originalQuestion) {
			return
		}

		// Only proceed if payload introduces a real change
		const didChange = Object.entries(payload).some(([key, value]) => {
			const prev = (originalQuestion as any)[key]
			return typeof value === 'object' && value !== null
				? JSON.stringify(prev) !== JSON.stringify(value)
				: prev !== value
		})
		if (!didChange) {
			return
		}

		// Update local state - always use EDIT_QUESTION with slideIndex
		dispatch({
			type: 'EDIT_QUESTION',
			difficulty: difficulty,
			slideIndex: slideIndex,
			questionIndex: questionIndex,
			payload: payload
		});

		// Call backend to persist edit to EduQuery
		// Use payload directly (not merged) to ensure new data from refinement is saved
		// The merged object uses originalQuestion which was captured before state update
		if (lessonId && originalQuestion.id) {
			try {
				// For assessment questions, sync both Pre and Post to database
				if (isAssessmentSlide) {
					// Extract base ID (remove _Pre-Assessment or _Post-Assessment suffix)
					const baseId = originalQuestion.id.replace(/_(Pre-Assessment|Post-Assessment)$/, '')

					// Sync both Pre and Post assessment versions
					const preId = `${baseId}_Pre-Assessment`
					const postId = `${baseId}_Post-Assessment`

					editQuestionEduquery({ lessonId, questionId: preId, question: { ...payload, id: preId } as Question })
					editQuestionEduquery({ lessonId, questionId: postId, question: { ...payload, id: postId } as Question })
				} else {
					// For non-assessment questions, just sync the single question
					editQuestionEduquery({ lessonId, questionId: originalQuestion.id, question: { ...originalQuestion, ...payload } as Question })
				}
			} catch {}
		}
	}, [dispatch, slideIndex, isAssessmentSlide, slideQuestions, lessonId, editQuestionEduquery]);

	const handleDelete = useCallback((questionIndex: number, difficulty: string, questionId: string) => {
		if (onDeleteQuestion) {
			onDeleteQuestion(questionIndex, difficulty, questionId)
		}
	}, [onDeleteQuestion])

	const allSectionApproved = slideQuestions.every(diffGroup =>
		diffGroup.questions.every(q => q.approved)
	)

	return (
		<div className="question-slide">
			<div className="question-slide-header">
				<h1 className="question-type">{sectionLabel(learningStage)}</h1>
				<button
					className={`approve-all-btn${allSectionApproved ? ' approved' : ''}`}
					onClick={onApproveAll}
					disabled={allSectionApproved}
				>
					Approve All
				</button>
			</div>

			<div className={`editing-warning ${learningStage === "Post-Assessment" || learningStage === "Pre-Assessment" ? "" : "display-none"}`}>
				NOTE: SAME QUESTIONS!
				You are about to edit both pre-assessment and post-assessment questions.
			</div>

			<>
				<DifficultyRow
					currentDifficulty={currentDifficulty}
					handleDifficultyChange={handleDifficultyChange} />
				<div className="question-list-container">
					{currentDifficultyQuestions?.questions.map((question, index) => (
						<QuestionContainer
							key={question.id}
							question={question}
                            onApprove={(approved) => handleApprove(index, currentDifficulty, approved)}
							onEdit={(payload) => handleEdit(index, currentDifficulty, payload)}
							canDelete={canDelete}
							onDelete={() => handleDelete(index, currentDifficulty, question.id)}
							canEdit={canEdit}
						/>
					))}
				</div>
			</>
		</div >
	)
}
